import { ApiService } from './api';

/**
 * Email Approval-Workflow Dashboard API.
 *
 * NOTE: these endpoints return PLAIN JSON objects, NOT the shared
 * `{status, statusCode, message, data}` envelope. So we read the response
 * body directly and do NOT unwrap `.data`.
 *
 * Base URL + Bearer auth are handled by the shared ApiService (see api.ts) —
 * with VITE_API_BASE_URL pointed at stage, `/email/workflows` resolves to
 * `https://stage-v2.getinfinitybox.com/v1/api/email/workflows`.
 *
 * Verified live (2026-07): scoping is real (tier1 user sees only their queue),
 * summary is NOT scoped, the tier-403 approve gate is NOT enforced yet (so the
 * UI must self-gate the Approve button), errors come back as {code, message}.
 */

const api = ApiService.getInstance();
const BASE = '/email/workflows';

/** Server-enforced proof-file limits (PR #209) — mirror client-side for clean UX. */
export const PROOF_EXTENSIONS = ['.pdf', '.docx', '.jpg', '.jpeg', '.png', '.webp'];
export const PROOF_MAX_BYTES = 15 * 1024 * 1024;

/** Returns an error message, or null if the file passes the proof-file limits. */
export const validateProofFile = (file: File): string | null => {
	const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
	if (!PROOF_EXTENSIONS.includes(ext)) return `Only ${PROOF_EXTENSIONS.join(' ')} files are accepted.`;
	if (file.size > PROOF_MAX_BYTES)
		return `File is ${(file.size / (1024 * 1024)).toFixed(1)} MB — the limit is 15 MB.`;
	return null;
};

export type WorkflowType = 'vendor_payment' | 'reimbursement' | 'ad_hoc_manpower' | 'unknown';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';
export type FinanceStatus = 'awaiting' | 'paid' | 'proof_attached' | 'recheck' | 'done';
export type TierRole = 'tier1' | 'tier2' | 'accounts' | null;
export type BoardView = 'pending' | 'raised' | 'history' | 'all';

/** One board row. Fields verified against the live stage response. */
export interface WorkflowRow {
	thread_id: string;
	account: string;
	workflow_type: WorkflowType;
	current_state: string;
	counterparty: string | null;
	counterparty_kind: 'company' | 'person' | 'unknown';
	counterparty_uncertain: number; // 1 = engine unsure
	attachment_amount: number | null;
	email_claimed_amount: number | null;
	amount_match: number | null; // 1 match, 0 MISMATCH, null n/a
	amount_source: string | null;
	amount_confidence: number | null;
	has_payment_proof: number;
	needs_review: number;
	// city (attribution)
	city_id: number | null;
	city: string | null;
	city_source: 'sender_seed' | 'email_derived' | 'approver_single' | 'uncertain' | null;
	// tier / chain
	facility_key: 'blr_sng' | 'blr_mhd' | 'hyd' | 'ggn' | 'mumbai' | null;
	current_tier: number | null;
	current_tier_role: TierRole;
	gist: string;
	created_at: string; // when INGESTED into the dashboard (not the email date)
	request_date?: string | null; // when approval was requested = first email ts (backend-added; optional until shipped)
	approval_status: ApprovalStatus; // RAW — filter matches this
	approval_source: 'email' | 'dashboard' | null;
	approved_by_name: string | null;
	approved_at: string | null;
	finance_status: FinanceStatus;
	finance_at: string | null;
	payment_proof_id: number | null;
	effective_approval_status: ApprovalStatus; // ← DISPLAY this
	effective_approval_source: 'email' | 'dashboard' | null;
}

export interface BoardResponse {
	page: number;
	page_size: number;
	total: number;
	rows: WorkflowRow[];
}

export interface BoardFilters {
	view?: BoardView;
	workflow_type?: WorkflowType;
	approval_status?: ApprovalStatus;
	finance_status?: FinanceStatus;
	account?: string;
	city_id?: number;
	needs_review?: boolean;
	q?: string;
	from?: string; // YYYY-MM-DD — range on created_at (ingest date)
	to?: string;
	// range on request_date (first-email/approval-requested date) — backend to honor once request_date ships.
	request_from?: string;
	request_to?: string;
	date_field?: 'created_at' | 'request_date'; // which date the from/to apply to
	page?: number;
	page_size?: number; // max 200
}

export interface WorkflowSummary {
	by_approval_status: Partial<Record<ApprovalStatus, number>>;
	by_finance_status: Partial<Record<FinanceStatus, number>>;
	by_workflow_type: Partial<Record<WorkflowType, number>>;
}

export interface ReportPeriod {
	period: string; // "2026-06" (month) or an ISO week label
	processed: number;
	amount: number;
}
export interface WorkflowReport {
	period: 'week' | 'month';
	periods: ReportPeriod[];
}
export interface CityReportRow {
	city_id: number | null;
	city: string | null;
	processed: number;
	amount: number;
}
export interface WorkflowCityReport {
	group_by: 'city';
	cities: CityReportRow[];
}

export interface WorkflowAttachment {
	id: number;
	source: string;
	filename: string;
	content_type: string;
	view_url: string; // secure proxy — fetch with auth → blob (NOT <img src>)
	url: string | null; // presigned S3 fallback
}
export interface WorkflowDecision {
	approval_status: ApprovalStatus;
	approval_source: string;
	approved_by_name: string | null;
	approved_at: string | null;
	approval_evidence: string | null;
	reply_draft: string | null;
	finance_status: FinanceStatus;
	finance_note: string | null;
	payment_proof_id: number | null;
}
export interface WorkflowDetail {
	state: Record<string, unknown> & {
		gist?: string;
		city?: string | null;
		facility_key?: string | null;
		current_tier?: number | null;
		current_tier_role?: TierRole;
		workflow_type?: WorkflowType;
		counterparty?: string | null;
	};
	decision: WorkflowDecision | null;
	proof: {
		id: number;
		source: string;
		filename: string;
		amount_seen: number | null;
		url: string | null;
	} | null;
	attachments: WorkflowAttachment[];
	effective_approval: { status: ApprovalStatus; source: 'email' | 'dashboard' | null };
}

export interface TimelineEvent {
	// verified against live: { type, ts, from, to, cc, subject, snippet, body_text, attachments }
	type?: string;
	ts?: string; // ISO timestamp
	from?: string; // "Name <email>"
	to?: string; // comma-separated "Name <email>"
	cc?: string;
	subject?: string;
	snippet?: string;
	body_text?: string;
	attachments?: unknown[];
	[k: string]: unknown;
}
export interface WorkflowTimeline {
	thread_id: string;
	counterparty: string | null;
	workflow_type: WorkflowType;
	amounts: {
		attachment_amount: string | number | null;
		email_claimed_amount: string | number | null;
		amount_match: number | null;
		amount_source: string | null;
	};
	gist: string;
	message_count: number;
	messages_source_thread?: string; // #197 fallback: which key actually served the emails
	events: TimelineEvent[];
}

export interface CreateWorkflowBody {
	workflow_type: 'vendor_payment' | 'reimbursement';
	counterparty: string;
	counterparty_kind: 'company' | 'person';
	/** Canonical payee id from the picker; omitted for free-typed new payees (server creates-if-new). */
	counterparty_id?: number | null;
	amount: number;
	purpose: string;
	note?: string;
	account_email?: string;
	proof: { source: 'dashboard_upload' | 'email_attachment'; filename?: string; attachment_id?: number };
}

/** GET /me — scope descriptor (contract v2). Drives the board's "All in my scope" toggle. */
export interface WorkflowScope {
	kind: 'requester' | 'approver' | 'finance' | 'leadership';
	cities: number[] | 'all';
	facilities: string[] | 'all';
	can_approve: boolean;
	approver_at: { facility_key: string; tier: number; role: string }[];
	finance: boolean;
}

export interface CounterpartyRow {
	id: number;
	name: string;
	kind: 'company' | 'person';
}
export interface CounterpartyGroups {
	city_id: number | null;
	groups: { mine: CounterpartyRow[]; other: CounterpartyRow[] };
}
export interface RoutingResult {
	current_tier: number;
	current_tier_role: TierRole;
	done?: boolean;
}

const qs = (filters: BoardFilters): string => {
	const p = new URLSearchParams();
	Object.entries(filters).forEach(([k, v]) => {
		if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
	});
	const s = p.toString();
	return s ? `?${s}` : '';
};

export const EmailWorkflowApi = {
	/** GET / — the board (filterable, paginated). Rows use effective_approval_status for display. */
	async getBoard(filters: BoardFilters = {}): Promise<BoardResponse> {
		return api.get(`${BASE}${qs(filters)}`) as unknown as Promise<BoardResponse>;
	},

	/** GET /me — who am I / how wide is my "all" (contract v2). 404s on old backends — callers must catch. */
	async getMe(): Promise<WorkflowScope> {
		return api.get(`${BASE}/me`) as unknown as Promise<WorkflowScope>;
	},

	/** GET /summary — status-count rollups (scoped to the viewer since contract v2). */
	async getSummary(): Promise<WorkflowSummary> {
		return api.get(`${BASE}/summary`) as unknown as Promise<WorkflowSummary>;
	},

	/** GET /counterparties — canonical payees, raiser's-city group first. */
	async getCounterparties(q: string): Promise<CounterpartyGroups> {
		return api.get(`${BASE}/counterparties?q=${encodeURIComponent(q)}`) as unknown as Promise<CounterpartyGroups>;
	},

	/** GET /counterparties/:id/purposes — recent purposes for the selected payee. */
	async getCounterpartyPurposes(id: number): Promise<{ purposes: string[] }> {
		return api.get(`${BASE}/counterparties/${id}/purposes`) as unknown as Promise<{ purposes: string[] }>;
	},

	/** GET /report?period=week|month — throughput per period. */
	async getReport(period: 'week' | 'month'): Promise<WorkflowReport> {
		return api.get(`${BASE}/report?period=${period}`) as unknown as Promise<WorkflowReport>;
	},

	/** GET /report?group_by=city — throughput grouped by attributed city. */
	async getCityReport(): Promise<WorkflowCityReport> {
		return api.get(`${BASE}/report?group_by=city`) as unknown as Promise<WorkflowCityReport>;
	},

	/** GET /:threadId — full detail. Read effective_approval, not decision, for email approvals. */
	async getDetail(threadId: string): Promise<WorkflowDetail> {
		return api.get(`${BASE}/${threadId}`) as unknown as Promise<WorkflowDetail>;
	},

	/** GET /:threadId/timeline — the email thread (events may be empty until the engine populates them). */
	async getTimeline(threadId: string): Promise<WorkflowTimeline> {
		return api.get(`${BASE}/${threadId}/timeline`) as unknown as Promise<WorkflowTimeline>;
	},

	/** POST /:threadId/approve — records decision + drafts reply (email NOT sent). */
	async approve(
		threadId: string,
		decision: 'approved' | 'rejected',
		note?: string
	): Promise<{ thread_id: string; approval_status: ApprovalStatus; reply_draft: string; sent: boolean; routing: RoutingResult | null }> {
		return api.post(`${BASE}/${threadId}/approve`, { decision, note }) as unknown as Promise<any>;
	},

	/** POST /:threadId/finance — mark paid/proof/recheck/done. 400 if not yet approved (except recheck). */
	async finance(
		threadId: string,
		finance_status: FinanceStatus,
		note?: string
	): Promise<{ thread_id: string; finance_status: FinanceStatus }> {
		return api.post(`${BASE}/${threadId}/finance`, { finance_status, note }) as unknown as Promise<any>;
	},

	/** POST /:threadId/proof — attach proof metadata (no browser→S3 upload in v1). */
	async attachProof(
		threadId: string,
		body: { source: 'dashboard_upload' | 'email_attachment'; filename?: string; attachment_id?: number; amount_seen?: number; note?: string }
	): Promise<{ thread_id: string; proof_id: number }> {
		return api.post(`${BASE}/${threadId}/proof`, body) as unknown as Promise<any>;
	},

	/**
	 * POST /:threadId/proof-file — upload the ACTUAL file (multipart) after create.
	 * Server enforces 15 MB max and .pdf .docx .jpg .jpeg .png .webp; stored in the
	 * sha-keyed S3 store and served back via the secure proxy (PR #209).
	 */
	async uploadProofFile(
		threadId: string,
		file: File,
		opts: { note?: string; amount_seen?: number } = {}
	): Promise<{ thread_id: string; proof_id: number }> {
		const fd = new FormData();
		fd.append('file', file);
		if (opts.note) fd.append('note', opts.note);
		if (opts.amount_seen != null) fd.append('amount_seen', String(opts.amount_seen));
		return api.postFormData(`${BASE}/${threadId}/proof-file`, fd) as unknown as Promise<any>;
	},

	/** POST / — create a request; server auto-routes to first tier. */
	async create(
		body: CreateWorkflowBody
	): Promise<{ thread_id: string; workflow_type: string; current_state: string; origin: string; routing: RoutingResult | null }> {
		return api.post(`${BASE}`, body) as unknown as Promise<any>;
	},

	/**
	 * Fetch a secure-proxy attachment as an object URL for in-app preview.
	 * The proxy serves as a download (CSP sandbox, nosniff) so we cannot point
	 * an <img src> at it — must fetch with auth then blob → object URL.
	 * Caller is responsible for URL.revokeObjectURL when done.
	 *
	 * SECURITY: these are UNTRUSTED files that arrived by email. When we re-wrap
	 * the bytes in a client-side blob we LOSE the proxy's protective headers
	 * (Content-Disposition: attachment, nosniff, CSP sandbox). A blob whose type
	 * is text/html would EXECUTE as HTML if window.open'd. So we FORCE the blob's
	 * MIME to an explicit safe type the caller vouches for (pdf/image). A caller
	 * that passes no safeType, or passes a non-allowlisted one, gets a generic
	 * octet-stream blob — which browsers download, never render/execute.
	 */
	async fetchAttachmentBlobUrl(viewUrl: string, safeType?: string): Promise<string> {
		// viewUrl is like "/v1/api/email/workflows/<tid>/attachment/<id>" — already absolute-from-host.
		const origin = new URL(import.meta.env.VITE_API_BASE_URL, window.location.origin).origin;
		const full = viewUrl.startsWith('http') ? viewUrl : `${origin}${viewUrl}`;
		const token = localStorage.getItem('auth_token') || '';
		const res = await fetch(full, { headers: { Authorization: `Bearer ${token}` } });
		if (!res.ok) throw new Error(`Attachment ${res.status}`);
		const raw = await res.blob();
		// allowlist of MIME types safe to render inline / in a blob tab (never text/html, svg, etc.)
		const SAFE = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp']);
		const type = safeType && SAFE.has(safeType) ? safeType : 'application/octet-stream';
		// re-type the bytes so a malicious content-type can't leak through the blob.
		const blob = new Blob([raw], { type });
		return URL.createObjectURL(blob);
	},
};
